import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { SITE } from "../../lib/config";
import { track, getReferralSource, isValidIndianMobile, normalizeIndianMobile, fireAdsConversion, GADS_CONVERSIONS } from "../../lib/analytics";

const inputCls =
  "w-full bg-white border border-[#E2E8E4] rounded-xl px-4 py-3 text-sm text-[#0A1C0E] outline-none focus:border-[#FDCD00] focus:ring-4 focus:ring-[#FDCD00]/25 transition-all placeholder:text-[#3A4D3E]/50";
const errCls = "border-red-400 focus:border-red-400 focus:ring-red-200";

function buildWhatsAppMessage(f) {
  const line = (label, val) => `${label}: ${val && String(val).trim() ? val : "-"}`;
  return [
    "Hello Chennai Packs,",
    "I would like to request a quotation.",
    "",
    "*Customer Details*",
    line("Name", f.name),
    line("Mobile", f.mobile),
    line("Company", f.company),
    line("Email", f.email),
    line("City", f.city),
    "",
    "*Product Details*",
    line("Product", f.product_name),
    line("SKU", f.sku),
    line("Quantity Required", f.quantity),
    line("Delivery Location", f.delivery_location),
    "",
    "*Additional Requirements:*",
    f.additional_requirements && f.additional_requirements.trim() ? f.additional_requirements : "-",
    "",
    "Please send your best quotation.",
    "Thank you.",
  ].join("\n");
}

export default function QuoteForm({ defaultProduct = "", defaultSku = "", compact = false, onDone }) {
  const [form, setForm] = useState({
    name: "", mobile: "", company: "", email: "", city: "",
    product_name: defaultProduct, sku: defaultSku, quantity: "",
    delivery_location: "", additional_requirements: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState("");
  const [waOpened, setWaOpened] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    setForm((f) => ({ ...f, product_name: defaultProduct, sku: defaultSku }));
  }, [defaultProduct, defaultSku]);

  // Auto-focus the Name field on mobile so buyers can start typing immediately.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    if (!isMobile) return;
    // Wait for the modal open animation to settle before focusing (prevents keyboard glitch).
    const t = setTimeout(() => {
      nameRef.current?.focus({ preventScroll: true });
    }, 350);
    return () => clearTimeout(t);
  }, []);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.mobile.trim()) e.mobile = "Please enter your mobile number.";
    else if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!form.quantity.trim()) e.quantity = "Please enter the quantity you need.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openWhatsApp = (url) => {
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (win) {
      track("WhatsApp Redirected", { product: form.product_name || "general" });
      setWaOpened(true);
      return true;
    }
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      mobile: normalizeIndianMobile(form.mobile),
      referral_source: getReferralSource(),
    };
    const url = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(buildWhatsAppMessage(payload))}`;
    track("Quote Form Submitted", { product: form.product_name || "general", sku: form.sku || "" });
    let apiOk = false;
    try {
      await api.createQuoteLead(payload);
      apiOk = true;
    } catch (err) {
      // Lead save failed — still let the customer continue on WhatsApp.
      console.error("Quote lead save failed:", err);
      toast.message("Continuing to WhatsApp with your details.");
    }
    // Google Ads conversion — fire ONLY when the backend confirms the lead was saved.
    if (apiOk) {
      fireAdsConversion(GADS_CONVERSIONS.quote_submitted, {
        transaction_id: `${Date.now()}-${form.mobile}`,
      });
    }
    setWaUrl(url);
    setLoading(false);
    setDone(true);
    onDone?.();
    setTimeout(() => openWhatsApp(url), 500);
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="quote-success">
        <div className="mx-auto w-16 h-16 rounded-full bg-[#4CAF50]/15 grid place-items-center animate-[cp-fade-up_0.5s_ease]">
          <CheckCircle2 size={40} className="text-[#1D6B2A]" />
        </div>
        <h3 className="font-head text-2xl font-semibold text-[#0A1C0E] mt-4">Thank you! Your enquiry has been received.</h3>
        <p className="text-[#3A4D3E] mt-2">
          {waOpened ? "We've opened WhatsApp with your details pre-filled." : "Continue the conversation on WhatsApp to get your quote faster."}
        </p>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("WhatsApp Redirected", { product: form.product_name || "general", manual: true })}
          data-testid="open-whatsapp-btn"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#1eb857] transition-colors"
        >
          <MessageCircle size={20} className="fill-white" /> Open WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" noValidate className="space-y-4">
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <input ref={nameRef} placeholder="Name *" value={form.name} onChange={set("name")} className={`${inputCls} ${errors.name ? errCls : ""}`} data-testid="quote-name" />
          {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="err-name">{errors.name}</p>}
        </div>
        <div>
          <input placeholder="Mobile Number *" type="tel" inputMode="numeric" autoComplete="tel" pattern="[0-9]*" maxLength={13} value={form.mobile} onChange={set("mobile")} className={`${inputCls} ${errors.mobile ? errCls : ""}`} data-testid="quote-mobile" />
          {errors.mobile
            ? <p className="text-xs text-red-500 mt-1" data-testid="err-mobile">{errors.mobile}</p>
            : <p className="text-[11px] text-[#3A4D3E]/70 mt-1" data-testid="mobile-hint">Enter your 10-digit mobile number — we'll add +91 automatically.</p>}
        </div>
        <input placeholder="Company Name" value={form.company} onChange={set("company")} className={inputCls} data-testid="quote-company" />
        <div>
          <input placeholder="Email" type="email" value={form.email} onChange={set("email")} className={`${inputCls} ${errors.email ? errCls : ""}`} data-testid="quote-email" />
          {errors.email && <p className="text-xs text-red-500 mt-1" data-testid="err-email">{errors.email}</p>}
        </div>
        <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="quote-city" />
        <input placeholder="Product Name" value={form.product_name} onChange={set("product_name")} className={inputCls} data-testid="quote-product" />
        <input type="hidden" value={form.sku} data-testid="quote-sku" />
        <div>
          <input placeholder="Quantity Required *" value={form.quantity} onChange={set("quantity")} className={`${inputCls} ${errors.quantity ? errCls : ""}`} data-testid="quote-quantity" />
          {errors.quantity && <p className="text-xs text-red-500 mt-1" data-testid="err-quantity">{errors.quantity}</p>}
        </div>
        <input placeholder="Delivery Location" value={form.delivery_location} onChange={set("delivery_location")} className={inputCls} data-testid="quote-delivery" />
      </div>
      <textarea rows={compact ? 2 : 3} placeholder="Additional Requirements" value={form.additional_requirements} onChange={set("additional_requirements")} className={inputCls} data-testid="quote-additional" />
      <div className="sticky bottom-0 -mx-6 md:mx-0 px-6 md:px-0 pt-3 pb-3 md:pt-0 md:pb-0 bg-white md:bg-transparent border-t md:border-t-0 border-[#E2E8E4]">
        <button
          type="submit"
          disabled={loading}
          data-testid="quote-submit"
          className="w-full bg-[#25D366] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#1eb857] transition-colors focus:ring-4 focus:ring-[#25D366]/30 flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? <Loader2 size={20} className="animate-spin" /> : <MessageCircle size={20} className="fill-white" />}
          {loading ? "Sending your enquiry…" : "Submit & Continue to WhatsApp"}
        </button>
        <p className="text-xs text-[#3A4D3E]/70 text-center mt-2">
          Your details are saved securely, then WhatsApp opens with your enquiry pre-filled.
        </p>
      </div>
    </form>
  );
}
